import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { getWalletStudentsPaginated, updateWalletStudent } from '../../api/endpoints/wallet';
import { formatCurrency } from '../../utils/formatCoins';
import { getErrorMessage } from '../../utils/errorHandler';
import { AlertMessages } from '../AlertMessages';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const WalletStudentsListTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // zero-based para TablePagination
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active'); // active | inactive | all
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [updatingId, setUpdatingId] = useState(null);
  const searchDebounceRef = useRef(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [newCardCode, setNewCardCode] = useState('');

  const showAlert = (type, message) => {
    setAlertState({
      show: true,
      type,
      message,
    });
  };

  const fetchData = async (override = {}) => {
    const currentPage = override.page !== undefined ? override.page : page;
    const currentPageSize = override.pageSize || pageSize;
    const currentSearch = override.search !== undefined ? override.search : search;
    const currentStatus = override.status || status;

    try {
      setLoading(true);
      const data = await getWalletStudentsPaginated({
        page: currentPage + 1, // API é 1-based
        pageSize: currentPageSize,
        search: currentSearch || undefined,
        status: currentStatus,
      });

      setRows(data.students || []);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Carregar inicialmente e quando page/pageSize/status mudarem
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status]);

  // Debounce para busca
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      fetchData({ page: 0, search: value });
    }, 400);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setPageSize(value);
    setPage(0);
  };

  const handleToggleStatus = async (student) => {
    const newStatus = !student.isActive;
    const confirmed = window.confirm(
      `Tem certeza que deseja ${newStatus ? 'ativar' : 'desativar'} o estudante "${student.name}"?`
    );
    if (!confirmed) return;

    try {
      setUpdatingId(student.id);
      await updateWalletStudent(student.id, { isActive: newStatus });
      showAlert('success', `Estudante ${newStatus ? 'ativado' : 'desativado'} com sucesso.`);
      fetchData();
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setUpdatingId(null);
    }
  };

  const openEditDialog = (student) => {
    setEditStudent(student);
    setNewCardCode(student.studentCode || '');
    setEditDialogOpen(true);
  };

  const handleConfirmEditCard = async () => {
    if (!editStudent) return;
    const trimmed = (newCardCode || '').trim();

    if (!trimmed) {
      showAlert('error', 'Informe o novo código/UUID do cartão.');
      return;
    }

    try {
      setUpdatingId(editStudent.id);
      await updateWalletStudent(editStudent.id, { studentCode: trimmed });
      showAlert('success', 'Cartão do estudante atualizado com sucesso.');
      setEditDialogOpen(false);
      setEditStudent(null);
      fetchData();
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setUpdatingId(null);
    }
  };

  const emptyMessage = useMemo(() => {
    if (loading) return 'Carregando estudantes...';
    if (search) return 'Nenhum estudante encontrado para o filtro informado.';
    if (status === 'active') return 'Nenhum estudante ativo com NFC cadastrado.';
    if (status === 'inactive') return 'Nenhum estudante inativo com NFC cadastrado.';
    return 'Nenhum estudante com NFC cadastrado.';
  }, [loading, search, status]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0b4f6c' }}>
              Estudantes com Cartão NFC
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulte, ative/desative e atualize o cartão dos estudantes de forma rápida.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Buscar por nome..."
              value={search}
              onChange={handleSearchChange}
              sx={{ minWidth: 220, bgcolor: 'white' }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 150, bgcolor: 'white' }}
            >
              <MenuItem value="active">Ativos</MenuItem>
              <MenuItem value="inactive">Inativos</MenuItem>
              <MenuItem value="all">Todos</MenuItem>
            </TextField>

            <Tooltip title="Recarregar lista">
              <span>
                <IconButton onClick={() => fetchData()} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 460, borderRadius: 2, bgcolor: '#fff' }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Código do Cartão</TableCell>
                <TableCell align="right">Saldo Atual</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
                      >
                        {student.studentCode}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {student.balance != null ? formatCurrency(student.balance) : 'R$ 0,00'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={student.isActive ? 'Ativo' : 'Inativo'}
                        color={student.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant={student.isActive ? 'outlined' : 'contained'}
                          color={student.isActive ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(student)}
                          disabled={updatingId === student.id}
                          sx={{ minWidth: 90 }}
                        >
                          {student.isActive ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Tooltip title="Alterar cartão (UUID)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(student)}
                              disabled={updatingId === student.id}
                              aria-label="Alterar cartão"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage="Itens por página"
        />
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar cartão do estudante</DialogTitle>
        <DialogContent dividers>
          {editStudent && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {editStudent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Código atual: {editStudent.studentCode || '—'}
              </Typography>
            </Box>
          )}

          <TextField
            label="Novo código/UUID do cartão"
            fullWidth
            margin="normal"
            value={newCardCode}
            onChange={(e) => setNewCardCode(e.target.value)}
            placeholder="Leia o novo cartão NFC ou digite o UUID"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updatingId != null}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmEditCard}
            variant="contained"
            disabled={updatingId != null}
            sx={{ bgcolor: '#1e958c', '&:hover': { bgcolor: '#0b4f6c' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};

